// This file is auto-generated by @hey-api/openapi-ts

import type { Options as ClientOptions, Client, TDataShape } from "./client";
import type {
  GetHealthData,
  GetHealthResponses,
  SignupData,
  SignupResponses,
  SignupErrors,
  LoginData,
  LoginResponses,
  LoginErrors,
  LogoutData,
  LogoutResponses,
  GetMeData,
  GetMeResponses,
  GetMeDetailData,
  GetMeDetailResponses,
  UpdateProfileData,
  UpdateProfileResponses,
  UpdateProfileErrors,
  ChangePasswordData,
  ChangePasswordResponses,
  ChangePasswordErrors,
  GetActivityData,
  GetActivityResponses,
  AdminListUsersData,
  AdminListUsersResponses,
  AdminDeleteUserData,
  AdminDeleteUserResponses,
  AdminDeleteUserErrors,
  AdminGetUserData,
  AdminGetUserResponses,
  AdminGetUserErrors,
  AdminUpdateUserData,
  AdminUpdateUserResponses,
  AdminUpdateUserErrors,
  AdminInviteUserData,
  AdminInviteUserResponses,
  AdminInviteUserErrors,
  AdminListActivityData,
  AdminListActivityResponses,
  AdminListActivityErrors,
  AdminActivityAnalyticsData,
  AdminActivityAnalyticsResponses,
  AdminActivityAnalyticsErrors,
  AdminActivityActionsData,
  AdminActivityActionsResponses,
  ListCasesData,
  ListCasesResponses,
  ListCasesErrors,
  GetCaseData,
  GetCaseResponses,
  GetCaseErrors,
  SetCaseStageData,
  SetCaseStageResponses,
  SetCaseStageErrors,
  UpdateCaseContentData,
  UpdateCaseContentResponses,
  UpdateCaseContentErrors,
  RegenerateCaseSlideData,
  RegenerateCaseSlideResponses,
  RegenerateCaseSlideErrors,
  SlackEventsData,
  SlackEventsResponses,
  SlackInteractionsData,
  SlackInteractionsResponses,
  DailyDealCheckData,
  DailyDealCheckResponses,
  JoinAllChannelsData,
  JoinAllChannelsResponses,
  LeaveSharedChannelsData,
  LeaveSharedChannelsResponses,
} from "./types.gen";
import { client } from "./client.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
> = ClientOptions<TData, ThrowOnError> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Health
 * Return a simple health status payload.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<GetHealthResponses, unknown, ThrowOnError>({
    url: "/health",
    ...options,
  });
};

/**
 * Auth Signup
 * Register a new user with email verification.
 */
export const signup = <ThrowOnError extends boolean = false>(
  options: Options<SignupData, ThrowOnError>,
) => {
  return (options.client ?? client).post<SignupResponses, SignupErrors, ThrowOnError>({
    url: "/auth/signup",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
};

/**
 * Auth Login
 * Authenticate via Supabase, then set the signed session cookie.
 *
 * The Supabase access/refresh tokens never leave the backend — the browser
 * only ever sees the HMAC cookie this endpoint sets.
 */
export const login = <ThrowOnError extends boolean = false>(
  options: Options<LoginData, ThrowOnError>,
) => {
  return (options.client ?? client).post<LoginResponses, LoginErrors, ThrowOnError>({
    url: "/auth/login",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
};

/**
 * Auth Logout
 * Sign out — clear the session cookie.
 */
export const logout = <ThrowOnError extends boolean = false>(
  options?: Options<LogoutData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<LogoutResponses, unknown, ThrowOnError>({
    url: "/auth/logout",
    ...options,
  });
};

/**
 * Auth Me
 * Get the current user's profile.
 */
export const getMe = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<GetMeResponses, unknown, ThrowOnError>({
    url: "/auth/me",
    ...options,
  });
};

/**
 * Auth Me Detail
 * Get the current user's profile with timeline fields.
 */
export const getMeDetail = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeDetailData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<GetMeDetailResponses, unknown, ThrowOnError>({
    url: "/auth/me/detail",
    ...options,
  });
};

/**
 * Auth Update Profile
 * Update current user's name.
 */
export const updateProfile = <ThrowOnError extends boolean = false>(
  options: Options<UpdateProfileData, ThrowOnError>,
) => {
  return (options.client ?? client).put<UpdateProfileResponses, UpdateProfileErrors, ThrowOnError>({
    url: "/auth/profile",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
};

/**
 * Auth Change Password
 * Change the current user's password (verifies current password first).
 */
export const changePassword = <ThrowOnError extends boolean = false>(
  options: Options<ChangePasswordData, ThrowOnError>,
) => {
  return (options.client ?? client).put<
    ChangePasswordResponses,
    ChangePasswordErrors,
    ThrowOnError
  >({
    url: "/auth/password",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
};

/**
 * Auth Activity
 * Get the current user's activity log.
 */
export const getActivity = <ThrowOnError extends boolean = false>(
  options?: Options<GetActivityData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<GetActivityResponses, unknown, ThrowOnError>({
    url: "/auth/activity",
    ...options,
  });
};

/**
 * Admin List Users
 * List all users (admin only).
 */
export const adminListUsers = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListUsersData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<AdminListUsersResponses, unknown, ThrowOnError>({
    url: "/admin/users",
    ...options,
  });
};

/**
 * Admin Delete User
 * Delete a user (admin only). Self-deletion blocked.
 */
export const adminDeleteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminDeleteUserData, ThrowOnError>,
) => {
  return (options.client ?? client).delete<
    AdminDeleteUserResponses,
    AdminDeleteUserErrors,
    ThrowOnError
  >({
    url: "/admin/users/{user_id}",
    ...options,
  });
};

/**
 * Admin Get User
 * Get a single user detail with activity logs (admin only).
 */
export const adminGetUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminGetUserData, ThrowOnError>,
) => {
  return (options.client ?? client).get<AdminGetUserResponses, AdminGetUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });
};

/**
 * Admin Update User
 * Update a user's name or role (admin only). Self-demotion blocked.
 */
export const adminUpdateUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminUpdateUserData, ThrowOnError>,
) => {
  return (options.client ?? client).put<
    AdminUpdateUserResponses,
    AdminUpdateUserErrors,
    ThrowOnError
  >({
    url: "/admin/users/{user_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
};

/**
 * Admin Invite User
 * Invite a user by email (admin only).
 */
export const adminInviteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminInviteUserData, ThrowOnError>,
) => {
  return (options.client ?? client).post<
    AdminInviteUserResponses,
    AdminInviteUserErrors,
    ThrowOnError
  >({
    url: "/admin/invite",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
};

/**
 * Admin List Activity
 * List activity across all users (admin only).
 *
 * Search, filtering, sorting, and pagination are all applied server-side by
 * the list_recent_activity RPC — the client sends params and renders results.
 */
export const adminListActivity = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListActivityData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    AdminListActivityResponses,
    AdminListActivityErrors,
    ThrowOnError
  >({
    url: "/admin/activity",
    ...options,
  });
};

/**
 * Admin Activity Analytics
 * Aggregated activity analytics for the admin dashboard (admin only).
 */
export const adminActivityAnalytics = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityAnalyticsData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<
    AdminActivityAnalyticsResponses,
    AdminActivityAnalyticsErrors,
    ThrowOnError
  >({
    url: "/admin/activity/analytics",
    ...options,
  });
};

/**
 * Admin Activity Actions
 * Distinct activity actions across all time (admin only).
 *
 * Used to populate the activity filter dropdown so it matches the full,
 * unbounded table scope rather than the windowed analytics breakdown.
 */
export const adminActivityActions = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityActionsData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<AdminActivityActionsResponses, unknown, ThrowOnError>({
    url: "/admin/activity/actions",
    ...options,
  });
};

/**
 * List Cases
 * List generated case studies, newest first, with optional filters.
 *
 * Rows are fetched broadly and filtered in Python — case volume is small,
 * which keeps this simpler than PostgREST jsonb filter syntax.
 *
 * Args:
 * session: Authenticated session (any logged-in user).
 * industry: Exact match against any entry of case_content.industry.
 * service_line: Exact match against any entry of case_content.service_lines.
 * region: Exact match against case_content.region.
 * year: Exact match against case_content.year.
 * stage: Exact match against the editorial stage (e.g. "Draft", "Published").
 * q: Case-insensitive substring match on client name or slide title.
 */
export const listCases = <ThrowOnError extends boolean = false>(
  options?: Options<ListCasesData, ThrowOnError>,
) => {
  return (options?.client ?? client).get<ListCasesResponses, ListCasesErrors, ThrowOnError>({
    url: "/cases",
    ...options,
  });
};

/**
 * Get Case
 * Get a single case study with its full extracted content.
 */
export const getCase = <ThrowOnError extends boolean = false>(
  options: Options<GetCaseData, ThrowOnError>,
) => {
  return (options.client ?? client).get<GetCaseResponses, GetCaseErrors, ThrowOnError>({
    url: "/cases/{case_id}",
    ...options,
  });
};

/**
 * Set Case Stage
 * Update a case's editorial stage and return the updated summary.
 *
 * Raises:
 * HTTPException: 404 when the case does not exist or is not in the
 * library; invalid stages are rejected with 422 by validation.
 */
export const setCaseStage = <ThrowOnError extends boolean = false>(
  options: Options<SetCaseStageData, ThrowOnError>,
) => {
  return (options.client ?? client).patch<SetCaseStageResponses, SetCaseStageErrors, ThrowOnError>({
    url: "/cases/{case_id}/stage",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
};

/**
 * Update Case Content
 * Apply a partial update to a case's extracted content.
 *
 * The request body carries any subset of CaseContent fields. They are merged
 * into the stored case_content and the merged result is validated against
 * CaseContent, so a partial edit can never leave the content in an invalid
 * shape. Audit columns record when and by whom the content was last edited.
 *
 * Args:
 * case_id: UUID of the cases_jobs row.
 * session: Authenticated session (any logged-in user).
 * body: Partial mapping of CaseContent field names to new values.
 *
 * Raises:
 * HTTPException: 404 when the case is missing or not in the library;
 * 422 for unknown fields, an empty body, values that fail
 * CaseContent validation, or slide text over the length cap.
 */
export const updateCaseContent = <ThrowOnError extends boolean = false>(
  options: Options<UpdateCaseContentData, ThrowOnError>,
) => {
  return (options.client ?? client).patch<
    UpdateCaseContentResponses,
    UpdateCaseContentErrors,
    ThrowOnError
  >({
    url: "/cases/{case_id}/content",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
};

/**
 * Regenerate Case Slide
 * Regenerate a case's pitch slide in the shared deck from its stored content.
 *
 * Replaces the existing slide when the row remembers one, otherwise appends
 * a fresh slide, then persists the new slide object ID.
 *
 * Raises:
 * HTTPException: 404 when the case is missing or not a library status;
 * 409 when the row has no (valid) case_content to build a slide from;
 * 502 when the Slides API call fails.
 */
export const regenerateCaseSlide = <ThrowOnError extends boolean = false>(
  options: Options<RegenerateCaseSlideData, ThrowOnError>,
) => {
  return (options.client ?? client).post<
    RegenerateCaseSlideResponses,
    RegenerateCaseSlideErrors,
    ThrowOnError
  >({
    url: "/cases/{case_id}/regenerate-slide",
    ...options,
  });
};

/**
 * Slack Events
 * Handle incoming Slack event and slash command payloads.
 *
 * Verifies the Slack request signature, responds to URL verification challenges,
 * and handles the /case-complete slash command. Slack requires a response within
 * 3 seconds, so the main extraction work is deferred to a background task.
 */
export const slackEvents = <ThrowOnError extends boolean = false>(
  options?: Options<SlackEventsData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<SlackEventsResponses, unknown, ThrowOnError>({
    url: "/slack/events",
    ...options,
  });
};

/**
 * Slack Interactions
 * Handle Slack interactive component callbacks (button clicks).
 *
 * Parses the interaction payload, updates the job row when both questions
 * are answered, and kicks off the full extraction pipeline.
 */
export const slackInteractions = <ThrowOnError extends boolean = false>(
  options?: Options<SlackInteractionsData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<SlackInteractionsResponses, unknown, ThrowOnError>({
    url: "/slack/interactions",
    ...options,
  });
};

/**
 * Daily Deal Check
 * Called daily by Railway cron to check for HubSpot deals closing this week.
 *
 * Requires an Authorization: Bearer <AUTH_SECRET> header to prevent
 * unauthorized calls.
 */
export const dailyDealCheck = <ThrowOnError extends boolean = false>(
  options?: Options<DailyDealCheckData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<DailyDealCheckResponses, unknown, ThrowOnError>({
    url: "/trigger/daily-check",
    ...options,
  });
};

/**
 * Join All Channels
 * One-time backfill: join every existing public channel in the workspace.
 *
 * Requires an Authorization: Bearer <AUTH_SECRET> header to prevent
 * unauthorized calls.
 */
export const joinAllChannels = <ThrowOnError extends boolean = false>(
  options?: Options<JoinAllChannelsData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<JoinAllChannelsResponses, unknown, ThrowOnError>({
    url: "/trigger/join-all-channels",
    ...options,
  });
};

/**
 * Leave Shared Channels
 * One-time cleanup: leave every externally shared (Slack Connect) channel.
 *
 * Requires an Authorization: Bearer <AUTH_SECRET> header to prevent
 * unauthorized calls.
 */
export const leaveSharedChannels = <ThrowOnError extends boolean = false>(
  options?: Options<LeaveSharedChannelsData, ThrowOnError>,
) => {
  return (options?.client ?? client).post<LeaveSharedChannelsResponses, unknown, ThrowOnError>({
    url: "/trigger/leave-shared-channels",
    ...options,
  });
};
